import React, { useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, theme as antdTheme, message } from 'antd';
import { ThemeProvider, GlobalStyles, AntdOverrides, ClaudeVars, claudeAntdTheme, useTheme } from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider, useTenant } from './context/TenantContext';
import { ProtectedRoute, RoleBasedRoute, LoadingSpinner, LandingRoute } from './components/common';
import { listTenants } from './services/api/cippApi';
import { fetchCustomerMappings } from './services/customerMappingService';
import { TIERS } from './constants/roles';
import { tenantsFromMappings, mergePickerTenants } from './utils/tenantsFromMappings';

// Pages
import {
  LoginPage,
  M365Page,
  SecurityPage,
  AcronisPage,
  ServiceDeskPage,
  ScalePadPage,
  DattoPage,
  ReportsPage,
  UnauthorizedPage,
  RecommendationsPage,
  KnowledgePage,
  SettingsPage,
  CustomerOnboardingPage,
  EngineerPerformancePage,
} from './pages';

const CustomerMappingsPage = lazy(() => import('./pages/admin/CustomerMappingsPage'));
// OD1N Walk-Round — iPad-first live capture (self-contained feature, lazy-loaded
// so Framer Motion / Dexie stay out of the main bundle until the route is hit).
const WalkRoundPage = lazy(() => import('./features/walkround/WalkRoundPage'));
// Agents — Claude-styled task-focused agent cards under the AI Assistant sidebar
// section (self-contained feature, lazy-loaded like Walk-Round above).
const AgentsPage = lazy(() => import('./features/agents/AgentsPage'));
const EngPerformanceAgentPage = lazy(() => import('./features/agents/EngPerformanceAgentPage'));
const TenderAgentPage = lazy(() => import('./features/agents/TenderAgentPage'));
const TsaReviewAgentPage = lazy(() => import('./features/agents/TsaReviewAgentPage'));
const ScopingAgentPage = lazy(() => import('./features/agents/ScopingAgentPage'));
const ScopingTrackerPage = lazy(() => import('./features/agents/ScopingTrackerPage'));
const ScopingCataloguePage = lazy(() => import('./features/agents/ScopingCataloguePage'));
// Invoice Reconcile — the Accounts section's single-pane, view-only reconcile
// grid (self-contained feature, lazy-loaded like the agents above).
const ReconcilePage = lazy(() => import('./features/reconcile/ReconcilePage'));
// Management Tasks — the management team's page (spec 2026-09-30).
const ManagementDashboardPage = lazy(() => import('./features/management/DashboardPage'));
const MyPage = lazy(() => import('./features/management/MyPage'));
const TaskPage = lazy(() => import('./features/management/TaskPage'));
const TeamPage = lazy(() => import('./features/management/TeamPage'));
const SignOffPage = lazy(() => import('./features/management/SignOffPage'));
const AllActionsPage = lazy(() => import('./features/management/AllActionsPage'));
const MeetingsPage = lazy(() => import('./features/management/MeetingsPage'));
const MeetingPage = lazy(() => import('./features/management/MeetingPage'));
const BinPage = lazy(() => import('./features/management/BinPage'));

/**
 * TenantLoader - Fetches tenants after successful authentication
 * This component sits inside both AuthProvider and TenantProvider
 * to access both authentication state and tenant management functions.
 */
const TenantLoader = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading, isInitialized, hasAnyRole } = useAuth();
  const {
    updateTenants,
    updateCustomerMappings,
    setIsLoading,
    setError,
    isLoading: tenantsLoading,
    tenants
  } = useTenant();
  // A boolean, not the hasAnyRole function: the function is rebuilt on every render and would
  // re-run the fetch effect in a loop while the tenant list stays empty.
  const canListCippTenants = hasAnyRole(TIERS.PLATFORMS);

  const fetchTenants = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // CIPP-MCP list_tenants is Platforms-only at the gateway (spec 2026-10-06 §11, Ruling H).
      // Everyone else builds the picker from the Odin-MCP customer mappings, with no error toast.
      if (!canListCippTenants) {
        try {
          const mappings = await fetchCustomerMappings();
          const rows = Array.isArray(mappings) ? mappings : [];
          updateCustomerMappings(rows);
          updateTenants(tenantsFromMappings(rows));
        } catch (mappingsError) {
          console.warn('[TenantLoader] Failed to fetch customer mappings for the tenant list:', mappingsError);
        }
        return;
      }

      // Fetch both CIPP tenants and Odin-MCP customer mappings in parallel
      const [tenantsResult, mappingsResult] = await Promise.allSettled([
        listTenants(),
        fetchCustomerMappings(),
      ]);

      // Process CIPP tenants response
      let tenantsData = [];
      if (tenantsResult.status === 'fulfilled') {
        const result = tenantsResult.value;

        // Handle the MCP response structure
        // The result may contain content array with text items
        if (result?.content) {
          // Parse MCP response format
          const textContent = result.content.find(c => c.type === 'text');
          if (textContent?.text) {
            try {
              const parsed = JSON.parse(textContent.text);
              // Handle both array and object with tenants property
              tenantsData = Array.isArray(parsed) ? parsed : (parsed.tenants || []);
            } catch (parseError) {
              console.error('Failed to parse tenants response:', parseError);
              throw new Error('Invalid tenant data format received');
            }
          }
        } else if (Array.isArray(result)) {
          // Direct array response
          tenantsData = result;
        } else if (result?.tenants) {
          // Object with tenants property
          tenantsData = result.tenants;
        }
      } else {
        console.error('[TenantLoader] Failed to fetch tenants:', tenantsResult.reason);
        throw tenantsResult.reason;
      }

      // Normalize tenant data to ensure consistent structure
      const normalizedTenants = tenantsData.map(tenant => ({
        id: tenant.id || tenant.tenantId || tenant.customerId,
        name: tenant.displayName || tenant.name || tenant.companyName || 'Unknown Tenant',
        domain: tenant.defaultDomainName || tenant.domain || null,
        ...tenant,
      }));

      // CIPP tenants plus the customers whose M365 CSG does not manage (spec 2026-10-19)
      updateTenants(mergePickerTenants(normalizedTenants, mappingsResult));

      // Process Odin-MCP customer mappings response
      if (mappingsResult.status === 'fulfilled') {
        const mappings = mappingsResult.value;
        if (Array.isArray(mappings)) {
          updateCustomerMappings(mappings);
          if (import.meta.env.DEV) {
            console.log(`[TenantLoader] Loaded ${mappings.length} customer mappings for hybrid platform ID resolution`);
          }
        }
      } else {
        // Customer mappings are optional - agent can fall back to fuzzy matching
        console.warn('[TenantLoader] Failed to fetch customer mappings (agent will use fuzzy matching):', mappingsResult.reason);
      }

      if (import.meta.env.DEV) {
        console.log(`[TenantLoader] Loaded ${normalizedTenants.length} tenants`);
      }
    } catch (err) {
      console.error('[TenantLoader] Failed to fetch tenants:', err);
      setError(err.message || 'Failed to load tenants');

      // Show user-friendly error message
      message.error({
        content: 'Unable to load tenant list. Please try refreshing the page.',
        duration: 5,
        key: 'tenant-fetch-error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [canListCippTenants, updateTenants, updateCustomerMappings, setIsLoading, setError]);

  // Fetch tenants when authentication succeeds
  useEffect(() => {
    // Only fetch when:
    // 1. MSAL is initialized
    // 2. Auth loading is complete
    // 3. User is authenticated
    // 4. We haven't loaded tenants yet
    if (isInitialized && !authLoading && isAuthenticated && tenants.length === 0) {
      fetchTenants();
    }
  }, [isInitialized, authLoading, isAuthenticated, tenants.length, fetchTenants]);

  // Show loading state while fetching tenants after authentication
  if (isAuthenticated && tenantsLoading && tenants.length === 0) {
    return (
      <LoadingSpinner
        fullScreen
        size="large"
        text="Loading tenants..."
      />
    );
  }

  return children;
};

// The ONE antd ConfigProvider: every antd menu, table, card, form control,
// drawer and modal takes its look from here (spec 2026-09-24 §2.4/§7).
const AntDesignThemeWrapper = ({ children }) => {
  const { isDarkMode } = useTheme();

  return (
    <ConfigProvider theme={claudeAntdTheme(isDarkMode, antdTheme)}>
      {children}
    </ConfigProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AntDesignThemeWrapper>
        <ClaudeVars />
        <GlobalStyles />
        <AntdOverrides />
        <AuthProvider>
          <TenantProvider>
            <TenantLoader>
              <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/unauthorized" element={<UnauthorizedPage />} />

              {/* Landing — every signed-in user; what it shows depends on the roles held */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <LandingRoute />
                  </ProtectedRoute>
                }
              />

              {/* Role-based routes */}
              <Route
                path="/m365"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.PLATFORMS}>
                    <M365Page />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/security"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.PLATFORMS}>
                    <SecurityPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/acronis"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.PLATFORMS}>
                    <AcronisPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/service-desk"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.PLATFORMS}>
                    <ServiceDeskPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/scalepad"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.PLATFORMS}>
                    <ScalePadPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/datto"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.PLATFORMS}>
                    <DattoPage />
                  </RoleBasedRoute>
                }
              />

              <Route
                path="/onboarding"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.ONBOARDING}>
                    <CustomerOnboardingPage />
                  </RoleBasedRoute>
                }
              />

              <Route
                path="/engineer-performance"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.ENGINEER_PERFORMANCE}>
                    <EngineerPerformancePage />
                  </RoleBasedRoute>
                }
              />

              {/* OD1N Walk-Round — iPad-first live capture (third onboarding modality).
                  TIERS.WALKROUND (own role; the sales team captures on site too). */}
              <Route
                path="/walk-round"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.WALKROUND}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Walk-Round..." />}>
                      <WalkRoundPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Agents — task-focused AI agent cards */}
              <Route
                path="/agents"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.AGENTS_HUB}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Agents..." />}>
                      <AgentsPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Engineer Performance agent — wizard → clarify → progress → result → history */}
              <Route
                path="/agents/engineer-performance"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.ENGINEER_PERFORMANCE}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Agent..." />}>
                      <EngPerformanceAgentPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Tender agent — intake → analysis → clarify → draft → artifacts */}
              <Route
                path="/agents/tender"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.TENDER}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Agent..." />}>
                      <TenderAgentPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* TSA Review agent — guided Q&A → LOB/parts → export (SoW,
                  parts list, task list). Guarded by TIERS.TSA. */}
              <Route
                path="/agents/tsa-review"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.TSA}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Agent..." />}>
                      <TsaReviewAgentPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Audit Request agent (codename: scoping) — estate signals → opportunity brief for TSAs.
                  TIERS.AUDIT_REQUEST: the sales team sees it too; the fleet-wide
                  sweep inside stays Admin-only (canSweepFleet). */}
              <Route
                path="/agents/audit-request"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.AUDIT_REQUEST}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Agent..." />}>
                      <ScopingAgentPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route path="/agents/scoping" element={<Navigate to="/agents/audit-request" replace />} />

              {/* Audit Request agent — Signal Tracker (Task 8 fills the body) */}
              <Route
                path="/agents/audit-request/tracker"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.AUDIT_REQUEST}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Agent..." />}>
                      <ScopingTrackerPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Audit Request agent — Signals Catalogue (Task 9 fills the body) */}
              <Route
                path="/agents/audit-request/signals"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.AUDIT_REQUEST}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Agent..." />}>
                      <ScopingCataloguePage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Redirect old routes to new routes for backwards compatibility */}
              <Route path="/backup" element={<Navigate to="/acronis" replace />} />
              <Route path="/assets" element={<Navigate to="/scalepad" replace />} />
              <Route
                path="/reports"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.REPORTS}>
                    <ReportsPage />
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/reports/recommendations"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.REPORTS}>
                    <RecommendationsPage />
                  </RoleBasedRoute>
                }
              />

              <Route
                path="/knowledge"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.ADMIN_ONLY}>
                    <KnowledgePage />
                  </RoleBasedRoute>
                }
              />

              {/* Accounts — Invoice Reconcile. */}
              <Route
                path="/accounts/reconcile"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.RECONCILE}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Invoice Reconcile..." />}>
                      <ReconcilePage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Management (spec 2026-10-02): one role, every route on TIERS.MANAGEMENT. */}
              <Route
                path="/management"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading Management..." />}>
                      <ManagementDashboardPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/management/my"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading your page..." />}>
                      <MyPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/management/all"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading actions..." />}>
                      <AllActionsPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/management/tasks/:id"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading task..." />}>
                      <TaskPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/management/team"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading team..." />}>
                      <TeamPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/management/meetings"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading meetings..." />}>
                      <MeetingsPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/management/meetings/:id"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading meeting..." />}>
                      <MeetingPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              <Route
                path="/management/sign-off"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading sign-off queue..." />}>
                      <SignOffPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />
              {/* The bin (spec 2026-10-09, amended 2026-10-13): every manager. */}
              <Route
                path="/management/bin"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.MANAGEMENT}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading the bin..." />}>
                      <BinPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.ADMIN_ONLY}>
                    <SettingsPage />
                  </RoleBasedRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin/customer-mappings"
                element={
                  <RoleBasedRoute allowedRoles={TIERS.ADMIN_ONLY}>
                    <Suspense fallback={<LoadingSpinner fullScreen text="Loading..." />}>
                      <CustomerMappingsPage />
                    </Suspense>
                  </RoleBasedRoute>
                }
              />

              {/* Catch all - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </TenantLoader>
          </TenantProvider>
        </AuthProvider>
      </AntDesignThemeWrapper>
    </ThemeProvider>
  );
}

export default App;
